import {IHolidaySettings} from "../../admin-panel";
import {BusinessCategoryEnum, BusinessSizeEnum, CurrentBookingMethodEnum} from "../../admin-panel";
import {EmployeeServiceMode} from "../../admin-panel/onboarding-setup.interface";

export type CommunicationTypes = 'sms' | 'whatsapp';

export interface IPrivateBusinessConfigurations {
  // How many clients this business has
  numOfClientsAllowed: number;
  /**
   * Height in pixels of one hour row in the admin calendar. Lets a business trade
   * density for legibility (long service names, notes) without touching the booking
   * site. Absent => {@link DEFAULT_CALENDAR_ROW_HEIGHT}.
   */
  calendarRowHeight?: number;
  /**
   * Whether staff members get an email with a calendar invite (ICS) when one of their
   * appointments is booked, updated or cancelled. Absent => enabled.
   */
  sendAppointmentEmailUpdates?: boolean;
}

/** The calendar's out-of-the-box hour-row height, in pixels. */
export const DEFAULT_CALENDAR_ROW_HEIGHT = 80;

/** Selectable row heights, from dense to roomy. */
export const CALENDAR_ROW_HEIGHT_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 56, label: 'צפוף' },
  { value: DEFAULT_CALENDAR_ROW_HEIGHT, label: 'רגיל' },
  { value: 110, label: 'מרווח' },
  { value: 150, label: 'גדול' },
  { value: 220, label: 'ענק' },
];

export interface IPublicBusinessConfigurations {
  // How many days ahead can we schedule appointments
  allowToSchedule: number;
  // Is the site locked or opened
  siteMode: 'open' | 'locked';
  startSchedulingDate: Date | string;
  sitePassword?: string;
  ctaText?: string;
  appointmentsStepGap?: 15 | 30 | 45 | 60 | 90 | 120;
  // Minutes of breathing room kept before and after every appointment (cleanup /
  // setup time). A new appointment can only start `appointmentsBufferMinutes`
  // after the previous one ends, and must end that long before the next begins.
  // 0 / undefined => back-to-back booking (legacy behaviour).
  appointmentsBufferMinutes?: number;
  allowToCancel: boolean;
  cancelHoursBeforeLimit?: number;
  sendReminderXDaysBefore: number;
  sendSameDayReminder?: boolean;
  sendConfirmationSMS: boolean;
  sendConfirmationSMSMessage?: string;
  showPayment?: boolean;

  // ============ NEW PAYMENT CONFIGURATION ============
  schedulePaymentMode?: 'none' | 'optional' | 'force';
  // 100 = full payment; anything lower is a partial deposit percentage
  scheduleAdvancedPaymentPercentage?: number;
  scheduleAllowVouchers?: boolean;
  scheduleAllowLoyaltyRewards?: boolean;
  // When to charge (PayMe only): immediately at booking, X hours before the
  // appointment (card tokenized at booking, auto-charged by cron), or 'save-card' —
  // card tokenized at booking, the business charges manually from the admin panel
  // (up to the booked amount; token kept 7 days past the appointment)
  scheduleChargeMode?: 'immediate' | 'before-appointment' | 'save-card';
  scheduleChargeHoursBefore?: number;
  // Who must pay: every booking, or only a customer's first booking (new customers)
  scheduleChargeScope?: 'always' | 'first-booking';
  // Free-text cancellation policy shown to the customer in the payment form
  scheduleCancellationPolicyText?: string;
  // How payment links reach the customer: SMS (default) or WhatsApp (falls back to SMS)
  paymentLinkChannel?: 'sms' | 'whatsapp';
  // How invoice/receipt links reach the customer (EZcount, and the manual "send invoice"
  // button). PayMe/SmartBee invoices go through the InvoiceReceipt automation instead.
  invoiceLinkChannel?: 'sms' | 'whatsapp';
  // How gift vouchers reach their recipient (owner-issued, re-sent, and bought online)
  voucherChannel?: 'sms' | 'whatsapp';
  // How the loyalty-club invite and the "welcome to the club" message are sent
  loyaltyMessagesChannel?: 'sms' | 'whatsapp';

  // ============ DEPRECATED PAYMENT FIELDS ============
  /** @deprecated Use schedulePaymentMode instead */
  isScheduleRequirePayment?: boolean;
  /** @deprecated Use scheduleAdvancedPaymentPercentage instead */
  schedulePaymentAmount?: number; // Can be a partial payment amount, or full payment (calculated from services)
  /** @deprecated User chooses at checkout */
  schedulePaymentType?: 'full' | 'partial'; // Whether to charge full service price or the fixed amount
  /** @deprecated Use schedulePaymentMode instead */
  isAdvancedPaymentRequired?: boolean;
  /** @deprecated */
  advancedPaymentAmount?: number;

  businessType: 'murshe' | 'patur';
  allowTashlumim: boolean;
  numOfTashlumim: number;
  hasMagicButton: boolean;
  isSendFeedbackMessage?: boolean;
  showReviewsOnWebsite?: boolean;
  googleMapsReviewsId?: string;
  isShowShopAfterAppointmentSchedule?: boolean;
  isShowShopInWebsite?: boolean;
  // Direct product ordering (pickup only, paid online). Default on — the flow is only
  // reachable when the business has a payment provider configured.
  enableDirectProductOrders?: boolean;
  // Optional business-specific pickup policy text shown at product checkout.
  productPickupPolicy?: string;
  appointmentManualApprove?: boolean;
  allowEmployeeToCreateServices?: boolean;
  isDisableScheduleSameDay?: boolean;
  // Offer off-grid slots (each free window's start, e.g. right after an appointment that
  // ends at 10:20) to customers on the booking website. Admin flows always show these; this
  // opts the public site in too. Defaults to off.
  allowOffGridSlotsOnWebsite?: boolean;
  sendFirstTimeCustomerSMS?: boolean;
  sendFirstTimeCustomerSMSMessage?: string;
  reminderSms: CommunicationTypes;
  sameDayReminderSms?: CommunicationTypes;
  waitingListSms?: CommunicationTypes;
  // How many days ahead a customer may ask to be notified about on the waiting list.
  // Drives both the website's date picker ceiling and the cron's search window.
  // Absent => ALLOW_TO_ENTER_WAITING_LIST_MAX.
  waitingListMaxDays?: number;
  appointmentConfirmationSms?: CommunicationTypes;
  holidaySettings?: IHolidaySettings;
  sendDelayNotifications?: boolean;
  // Channel for the "your appointment may be delayed" notice
  delayNotificationsChannel?: CommunicationTypes;
  metaPixelId?: string;
  iframeMode?: boolean;
  allowMultipleServices?: boolean;
  adminAiEnabled?: boolean;
  businessCategory?: BusinessCategoryEnum[];
  businessSize?: BusinessSizeEnum;
  currentBookingMethod?: CurrentBookingMethodEnum;
  employeeServiceMode?: EmployeeServiceMode;
  aiVoicePreferences?: IAiVoicePreferences;
}

export interface IAiVoicePreferences {
  ownerGender: 'male' | 'female';
  preferredVoiceGender: 'male' | 'female';
}

export interface IBusinessConfigurations extends IPrivateBusinessConfigurations, IPublicBusinessConfigurations {
}
